import { ReactNode } from 'react';
import { Button, Divider, Flex, Space, Typography } from 'antd';
import { ListFilter } from 'lucide-react';
import { TbPlus, TbTrash } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DragDropContext, Draggable, DragHandle, Droppable, type DropResult } from '@/shared/components/drag-and-drop';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import MiddleDot from '@/shared/components/typography/MiddleDot';

import {
    ADVANCED_FILTER_LIMITS,
    createAdvancedFilterCondition,
    createAdvancedFilterGroup,
    type AdvancedFilterCondition,
    type AdvancedFilterDefinition,
    type AdvancedFilterGroup,
} from '../advancedFilters';
import FilterBooleanJoinSelect from '../FilterBooleanJoinSelect';

const GROUP_DROPPABLE_ID = 'advanced-filter-groups';
const GROUP_DRAG_TYPE = 'ADVANCED_FILTER_GROUP';
const CONDITION_DRAG_TYPE = 'ADVANCED_FILTER_CONDITION';
const conditionDroppableId = (groupId: string) => `advanced-filter-conditions:${groupId}`;
const groupIdFromConditionDroppable = (droppableId: string) =>
    droppableId.startsWith('advanced-filter-conditions:')
        ? droppableId.slice('advanced-filter-conditions:'.length)
        : undefined;

interface AdvancedFilterConditionRenderProps<TRule> {
    condition: AdvancedFilterCondition<TRule>;
    group: AdvancedFilterGroup<TRule>;
    remove: () => void;
    update: (rule: TRule) => void;
}

interface AdvancedFilterAddRenderProps<TRule> {
    disabled: boolean;
    group: AdvancedFilterGroup<TRule>;
    onAdd: (rule: TRule) => void;
}

export interface AdvancedFilterEditorProps<TRule> {
    definition: AdvancedFilterDefinition<TRule>;
    onChange: (definition: AdvancedFilterDefinition<TRule>) => void;
    renderAddCondition: (props: AdvancedFilterAddRenderProps<TRule>) => ReactNode;
    renderCondition: (props: AdvancedFilterConditionRenderProps<TRule>) => ReactNode;
}

const reorder = <T,>(items: T[], sourceIndex: number, destinationIndex: number) => {
    const next = [...items];
    const [moved] = next.splice(sourceIndex, 1);

    if (moved !== undefined) {
        next.splice(destinationIndex, 0, moved);
    }

    return next;
};

const AdvancedFilterEditor = <TRule,>({
    definition,
    onChange,
    renderAddCondition,
    renderCondition,
}: AdvancedFilterEditorProps<TRule>): JSX.Element => {
    const { token } = useAntDesignTheme();
    const conditionCount = definition.groups.reduce((count, group) => count + group.conditions.length, 0);

    const updateGroups = (groups: AdvancedFilterGroup<TRule>[]) => onChange({ ...definition, groups });
    const updateGroup = (groupId: string, update: (group: AdvancedFilterGroup<TRule>) => AdvancedFilterGroup<TRule>) =>
        updateGroups(definition.groups.map((group) => (group.id === groupId ? update(group) : group)));
    const removeGroup = (groupId: string) => updateGroups(definition.groups.filter((group) => group.id !== groupId));

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        if (result.type === GROUP_DRAG_TYPE) {
            updateGroups(reorder(definition.groups, result.source.index, result.destination.index));
            return;
        }

        if (result.type !== CONDITION_DRAG_TYPE) {
            return;
        }

        const sourceGroupId = groupIdFromConditionDroppable(result.source.droppableId);
        const destinationGroupId = groupIdFromConditionDroppable(result.destination.droppableId);

        if (!sourceGroupId || !destinationGroupId) {
            return;
        }

        const sourceGroup = definition.groups.find((group) => group.id === sourceGroupId);
        const destinationGroup = definition.groups.find((group) => group.id === destinationGroupId);

        if (!sourceGroup || !destinationGroup) {
            return;
        }

        if (sourceGroupId === destinationGroupId) {
            updateGroup(sourceGroupId, (group) => ({
                ...group,
                conditions: reorder(group.conditions, result.source.index, result.destination?.index ?? 0),
            }));
            return;
        }

        const nextSourceConditions = [...sourceGroup.conditions];
        const [moved] = nextSourceConditions.splice(result.source.index, 1);

        if (!moved) {
            return;
        }

        const nextDestinationConditions = [...destinationGroup.conditions];
        nextDestinationConditions.splice(result.destination.index, 0, moved);
        updateGroups(
            definition.groups.map((group) => {
                if (group.id === sourceGroupId) {
                    return { ...group, conditions: nextSourceConditions };
                }
                if (group.id === destinationGroupId) {
                    return { ...group, conditions: nextDestinationConditions };
                }
                return group;
            }),
        );
    };

    return (
        <DragDropContext boundary='context' onDragEnd={handleDragEnd}>
            <Droppable
                droppableId={GROUP_DROPPABLE_ID}
                type={GROUP_DRAG_TYPE}
                decoratePlaceholder
                placeholderMode='measured'
            >
                {(groupProvided) => (
                    <div ref={groupProvided.innerRef} {...groupProvided.droppableProps}>
                        <Space direction='vertical' size={8} style={{ display: 'flex' }}>
                            {definition.groups.length === 0 && (
                                <EmptyView
                                    description='Group related conditions to control exactly which results appear.'
                                    height='10rem'
                                    icon={<ListFilter size='0.875rem' />}
                                    label='Start With A Filter Group'
                                />
                            )}
                            {definition.groups.map((group, groupIndex) => (
                                <Draggable draggableId={group.id} index={groupIndex} key={group.id}>
                                    {(groupDragProvided, groupSnapshot) => (
                                        <div
                                            ref={groupDragProvided.innerRef}
                                            {...groupDragProvided.draggableProps}
                                            style={{
                                                ...groupDragProvided.draggableProps.style,
                                                alignItems: 'start',
                                                display: 'grid',
                                                gap: '0.5rem',
                                                gridTemplateColumns: '4.5rem minmax(0, 1fr)',
                                            }}
                                        >
                                            <Flex align='center' justify='flex-start' style={{ minHeight: 24 }}>
                                                {groupIndex === 0 ? (
                                                    <DropdownButton
                                                        disabled
                                                        size='small'
                                                        type='dashed'
                                                        style={{
                                                            flex: 'none',
                                                            minWidth: '4.5rem',
                                                        }}
                                                    >
                                                        Where
                                                    </DropdownButton>
                                                ) : (
                                                    <FilterBooleanJoinSelect
                                                        value={group.join}
                                                        onChange={(join) =>
                                                            updateGroup(group.id, (value) => ({ ...value, join }))
                                                        }
                                                    />
                                                )}
                                            </Flex>
                                            <div
                                                style={{
                                                    background: token.colorFillQuaternary,
                                                    border: `1px solid ${token.colorBorderSecondary}`,
                                                    borderRadius: token.borderRadius,
                                                    minWidth: 0,
                                                    padding: '0.75rem',
                                                }}
                                            >
                                                <Flex align='center' gap='small' justify='space-between'>
                                                    <Flex align='center' gap='small'>
                                                        <DragHandle
                                                            dragHandleProps={groupDragProvided.dragHandleProps}
                                                            isDragging={groupSnapshot.isDragging}
                                                            label={`Reorder filter group ${groupIndex + 1}`}
                                                        />
                                                        <Typography.Text strong>Group {groupIndex + 1}</Typography.Text>
                                                        <MiddleDot horizontalMargin='0.125rem' />
                                                        <Typography.Text type='secondary'>
                                                            {group.conditions.length}{' '}
                                                            {group.conditions.length === 1 ? 'Condition' : 'Conditions'}
                                                        </Typography.Text>
                                                    </Flex>
                                                    <Button
                                                        aria-label={`Remove filter group ${groupIndex + 1}`}
                                                        icon={<TbTrash style={{ color: token.colorTextTertiary }} />}
                                                        size='small'
                                                        type='text'
                                                        onClick={() => removeGroup(group.id)}
                                                    />
                                                </Flex>
                                                <Droppable
                                                    droppableId={conditionDroppableId(group.id)}
                                                    type={CONDITION_DRAG_TYPE}
                                                    decoratePlaceholder
                                                    placeholderMode='measured'
                                                >
                                                    {(conditionProvided) => (
                                                        <div
                                                            ref={conditionProvided.innerRef}
                                                            {...conditionProvided.droppableProps}
                                                            style={{ marginTop: '0.625rem' }}
                                                        >
                                                            <Space
                                                                direction='vertical'
                                                                size={6}
                                                                style={{ display: 'flex' }}
                                                            >
                                                                {group.conditions.length === 0 && (
                                                                    <EmptyView
                                                                        description='Choose a property, operator, and value to decide which results this group includes.'
                                                                        height='8rem'
                                                                        icon={<ListFilter size='0.875rem' />}
                                                                        label='Add This Group’s First Condition'
                                                                    />
                                                                )}
                                                                {group.conditions.map((condition, conditionIndex) => (
                                                                    <Draggable
                                                                        draggableId={condition.id}
                                                                        index={conditionIndex}
                                                                        key={condition.id}
                                                                    >
                                                                        {(conditionDragProvided, conditionSnapshot) => (
                                                                            <Flex
                                                                                ref={conditionDragProvided.innerRef}
                                                                                {...conditionDragProvided.draggableProps}
                                                                                align='center'
                                                                                gap='small'
                                                                                style={{
                                                                                    ...conditionDragProvided
                                                                                        .draggableProps.style,
                                                                                    minWidth: 0,
                                                                                }}
                                                                            >
                                                                                <DragHandle
                                                                                    dragHandleProps={
                                                                                        conditionDragProvided.dragHandleProps
                                                                                    }
                                                                                    isDragging={
                                                                                        conditionSnapshot.isDragging
                                                                                    }
                                                                                    label={`Reorder condition ${conditionIndex + 1}`}
                                                                                    style={{ height: 24, minWidth: 24 }}
                                                                                />
                                                                                {conditionIndex > 0 ? (
                                                                                    <>
                                                                                        <FilterBooleanJoinSelect
                                                                                            value={condition.join}
                                                                                            onChange={(join) =>
                                                                                                updateGroup(
                                                                                                    group.id,
                                                                                                    (value) => ({
                                                                                                        ...value,
                                                                                                        conditions:
                                                                                                            value.conditions.map(
                                                                                                                (
                                                                                                                    item,
                                                                                                                ) =>
                                                                                                                    item.id ===
                                                                                                                    condition.id
                                                                                                                        ? {
                                                                                                                              ...item,
                                                                                                                              join,
                                                                                                                          }
                                                                                                                        : item,
                                                                                                            ),
                                                                                                    }),
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                        <Divider
                                                                                            type='vertical'
                                                                                            style={{
                                                                                                height: 16,
                                                                                                margin: 0,
                                                                                            }}
                                                                                        />
                                                                                    </>
                                                                                ) : null}
                                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                                    {renderCondition({
                                                                                        condition,
                                                                                        group,
                                                                                        remove: () =>
                                                                                            updateGroup(
                                                                                                group.id,
                                                                                                (value) => ({
                                                                                                    ...value,
                                                                                                    conditions:
                                                                                                        value.conditions.filter(
                                                                                                            (item) =>
                                                                                                                item.id !==
                                                                                                                condition.id,
                                                                                                        ),
                                                                                                }),
                                                                                            ),
                                                                                        update: (rule) =>
                                                                                            updateGroup(
                                                                                                group.id,
                                                                                                (value) => ({
                                                                                                    ...value,
                                                                                                    conditions:
                                                                                                        value.conditions.map(
                                                                                                            (item) =>
                                                                                                                item.id ===
                                                                                                                condition.id
                                                                                                                    ? {
                                                                                                                          ...item,
                                                                                                                          rule,
                                                                                                                      }
                                                                                                                    : item,
                                                                                                        ),
                                                                                                }),
                                                                                            ),
                                                                                    })}
                                                                                </div>
                                                                            </Flex>
                                                                        )}
                                                                    </Draggable>
                                                                ))}
                                                                {conditionProvided.placeholder}
                                                                {renderAddCondition({
                                                                    disabled:
                                                                        group.conditions.length >=
                                                                            ADVANCED_FILTER_LIMITS.conditionsPerGroup ||
                                                                        conditionCount >=
                                                                            ADVANCED_FILTER_LIMITS.totalConditions,
                                                                    group,
                                                                    onAdd: (rule) =>
                                                                        updateGroup(group.id, (value) => ({
                                                                            ...value,
                                                                            conditions: [
                                                                                ...value.conditions,
                                                                                createAdvancedFilterCondition(rule),
                                                                            ],
                                                                        })),
                                                                })}
                                                            </Space>
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {groupProvided.placeholder}
                            <Button
                                block
                                disabled={definition.groups.length >= ADVANCED_FILTER_LIMITS.groups}
                                icon={<TbPlus />}
                                size='small'
                                type='dashed'
                                onClick={() => updateGroups([...definition.groups, createAdvancedFilterGroup<TRule>()])}
                            >
                                Add Filter Group
                            </Button>
                        </Space>
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default AdvancedFilterEditor;
